import Box from "@mui/material/Box";
import type {RowData} from "@tanstack/react-table";
import {hexToRgba} from "crqlar-react-common/dist/utils/color.util";
import {CommonThemeColors} from "crqlar-react-common/dist/components/theme";
import {memo, useCallback, useEffect, useLayoutEffect, useRef, useState} from "react";
import type {CSSProperties} from "react";
import ServerTableLoadingProgress from "./components/ServerTableLoadingProgress";
import ServerTableBody from "./ServerTableBody";
import {useServerTableContext} from "./ServerTableContext";
import ServerTableHeader from "./ServerTableHeader";
import ServerTablePagination from "./pagination/ServerTablePagination";
import type {ServerTableEmptyStateType, ServerTableProps} from "./types";
import {getEffectiveColumnWidth, getEffectiveColumnWidths, getVisibleColumns} from "./utils";

// Column widths live in the colgroup, so body rows can remain frozen during header resizing.
const FreezableServerTableBody = memo(
    ServerTableBody,
    (_previous, next) => !!next.table.getState().columnSizingInfo.isResizingColumn
) as typeof ServerTableBody;

/** Renders table content and pagination from the nearest ServerTableProvider. */
const ServerTable = ({
    loadingResetKey,
    stickyHeader,
    rowMinHeight = 44,
    layout,
    showPagination = true
}: ServerTableProps) => {
    const {table, query, presentation, isSearchActive, filterRules, onRowClick} = useServerTableContext();
    const {isLoading, isFetching, isError, isPreviousData} = query;

    // The preset is declared once on the controller; these are its view defaults, and an
    // explicit prop still wins. Embedded tables grow to their rows and let the surrounding
    // surface own scrolling, which leaves a sticky header nothing to stick to.
    const embedded = presentation === 'embedded';
    const effectiveLayout = layout ?? (embedded ? 'content' : 'fill');
    const effectiveStickyHeader = stickyHeader ?? !embedded;

    const onRowClickRef = useRef(onRowClick);
    const initialContentRevealedRef = useRef(false);
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);

    const [initialContentRevealed, setInitialContentRevealed] = useState(false);
    const [containerWidth, setContainerWidth] = useState(0);

    // Keep the memoized body stable when only the caller's row callback changes.
    onRowClickRef.current = onRowClick;
    const handleRowClick = useCallback((row: RowData) => onRowClickRef.current?.(row), []);

    // Pinned widths are capped against the current scroll-container width.
    useLayoutEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        let animationFrame: number | undefined;
        const updateWidth = () => {
            const nextWidth = container.clientWidth;
            setContainerWidth(current => current === nextWidth ? current : nextWidth);
        };
        const scheduleWidthUpdate = () => {
            if (animationFrame !== undefined) window.cancelAnimationFrame(animationFrame);
            animationFrame = window.requestAnimationFrame(updateWidth);
        };
        updateWidth();
        const observer = new ResizeObserver(scheduleWidthUpdate);
        observer.observe(container);
        return () => {
            observer.disconnect();
            if (animationFrame !== undefined) window.cancelAnimationFrame(animationFrame);
        };
    }, []);

    useEffect(() => {
        if (isLoading || initialContentRevealedRef.current) return;

        // Mount resolved rows and pagination once before revealing them together.
        const animationFrame = window.requestAnimationFrame(() => {
            initialContentRevealedRef.current = true;
            setInitialContentRevealed(true);
        });

        return () => window.cancelAnimationFrame(animationFrame);
    }, [isLoading]);

    const visibleColumns = getVisibleColumns(table);
    const columnSizing = table.getState().columnSizing;

    // A user-resized column keeps its explicit width and no longer absorbs spare space.
    const growColumn = visibleColumns.find(column =>
        !column.getIsPinned() && column.columnDef.meta?.grow && columnSizing[column.id] === undefined
    );

    const growColumnId = growColumn?.id;
    const columnCount = visibleColumns.length;
    const effectiveColumnWidths = getEffectiveColumnWidths(table, containerWidth);

    // Sticky offsets accumulate rendered, possibly capped widths rather than raw sizes.
    const pinnedColumnStartVars = {} as Record<string, string>;
    let pinnedLeft = 0;

    table.getLeftVisibleLeafColumns().forEach((column, index) => {
        pinnedColumnStartVars[`--server-table-pinned-left-${index}`] = `${pinnedLeft}px`;
        pinnedLeft += getEffectiveColumnWidth(column, effectiveColumnWidths);
    });

    const tableMinWidth = visibleColumns.reduce(
        (sum, column) => sum + getEffectiveColumnWidth(column, effectiveColumnWidths),
        0
    );
    const rowHoverColor = hexToRgba(CommonThemeColors.sys.primary.main, 0.025);
    const emptyState: ServerTableEmptyStateType = isSearchActive
        ? 'search'
        : filterRules.length ? 'filter' : 'data';
    const rowClick = onRowClick && !isPreviousData ? handleRowClick : undefined;
    const contentLayout = effectiveLayout === 'content';

    // Sticky headers require a table-owned scroll container.
    const headerSticky = effectiveStickyHeader && !contentLayout;

    const meaningfulFetch = !!isFetching && (!!isLoading || !!isPreviousData);

    return (
        <Box sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            flex: contentLayout ? '0 0 auto' : 1,
            minHeight: 0,
            minWidth: 0,
            overflow: contentLayout ? 'visible' : 'hidden'
        }}>
            <ServerTableLoadingProgress active={meaningfulFetch} resetKey={loadingResetKey}/>

            <Box
                ref={scrollContainerRef}
                // Expose the visible (not scrolled) width so overlays like the empty state can pin to it.
                style={{'--server-table-viewport-width': `${containerWidth}px`} as CSSProperties}
                sx={{
                    flex: contentLayout ? '0 0 auto' : 1,
                    minHeight: 0,
                    minWidth: 0,
                    overflow: contentLayout ? 'visible' : 'auto',
                    WebkitOverflowScrolling: contentLayout ? undefined : 'touch',
                    overscrollBehaviorX: contentLayout ? undefined : 'contain',
                    touchAction: contentLayout ? undefined : 'pan-x pan-y'
                }}
            >
                <Box
                    component="table"
                    style={pinnedColumnStartVars as CSSProperties}
                    sx={{
                        width: '100%',
                        minWidth: tableMinWidth,
                        tableLayout: 'fixed',
                        borderCollapse: 'separate',
                        borderSpacing: 0
                    }}
                >
                    <colgroup>
                        {visibleColumns.map(column => (
                            <col
                                key={column.id}
                                style={column.id === growColumnId
                                    ? undefined
                                    : {width: getEffectiveColumnWidth(column, effectiveColumnWidths)}}
                            />
                        ))}
                    </colgroup>

                    <ServerTableHeader
                        table={table}
                        sticky={headerSticky}
                    />

                    <FreezableServerTableBody
                        table={table}
                        isLoading={isLoading}
                        isFetching={meaningfulFetch}
                        isError={isError}
                        initialContentRevealed={initialContentRevealed}
                        columnCount={columnCount}
                        rowMinHeight={rowMinHeight}
                        rowHoverColor={rowHoverColor}
                        emptyState={emptyState}
                        presentation={presentation}
                        onRowClick={rowClick}
                    />
                </Box>

                {showPagination && !isLoading && (
                    <ServerTablePagination
                        table={table}
                        presentation={presentation}
                        initialContentRevealed={initialContentRevealed}
                    />
                )}
            </Box>
        </Box>
    );
};

export default ServerTable;
